<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { Config } from '@/Config'
import * as Server from '@/services/Server'
import PageHeader from '@/components/PageHeader.vue'
import ProvisionalMark from '@/components/ProvisionalMark.vue'
import ChipEventType from '@/components/ChipEventType.vue'
import DateRange from '@/components/DateRange.vue'
import UserName from '@/components/UserName.vue'
import TrackTitle from '@/components/TrackTitle.vue'
import { podiumClass } from '@/lib/podium'
import { formatDelta } from '@/lib/rating'
import { useTrackTitle } from '@/composables/useTrackTitle'

/**
 * One track of an event: everybody's result on that track, in position order.
 * The positions come from the event ranking, so this table matches the per-track position shown
 * in the event detail.
 */
const route = useRoute()
const { t } = useI18n()
const { primaryName } = useTrackTitle()

const eventId = computed(() => route.params.id)
const trackKey = computed(() => route.params.trackKey)

const data = ref(null)
const loading = ref(true)
const error = ref(false)

const STATE_COLORS = {
  scheduled: 'blue-grey',
  running: 'green',
  finished: 'grey',
}

const stateColor = (state) => STATE_COLORS[state] ?? 'grey'

const breadcrumbs = computed(() => [
  { title: t('events.breadcrumb'), to: { name: 'events' } },
  {
    title: data.value ? t(`eventTypes.${data.value.event.eventType}`) : `#${eventId.value}`,
    to: { name: 'event_detail', params: { id: eventId.value } },
  },
  { title: data.value ? primaryName(data.value.track.trackKey) : trackKey.value },
])

const fetchTrack = () => {
  loading.value = true
  error.value = false

  Server.get(Config.server.routes.api.events_track(eventId.value, trackKey.value))
    .then(response => {
      data.value = response.data?.data ?? null
    })
    .catch(() => {
      error.value = true
      data.value = null
    })
    .finally(() => {
      loading.value = false
    })
}

onMounted(fetchTrack)
watch([eventId, trackKey], fetchTrack)
</script>

<template>
  <main>
    <v-container>
      <v-breadcrumbs :items="breadcrumbs" class="px-0 pb-2">
        <template v-slot:divider>
          <v-icon>mdi-chevron-right</v-icon>
        </template>
      </v-breadcrumbs>

      <div v-if="loading" class="d-flex justify-center py-12">
        <v-progress-circular indeterminate color="primary" />
      </div>

      <v-alert v-else-if="error" type="error" variant="tonal">
        {{ $t('events.loadError') }}
      </v-alert>

      <template v-else-if="data">
        <PageHeader :title="primaryName(data.track.trackKey)" icon="mdi-flag-checkered">
          <template #title>
            <TrackTitle :track-id="data.track.trackKey" variant="stacked" />
          </template>
        </PageHeader>

        <v-card elevation="3" rounded="lg" class="mb-6 overflow-hidden">
          <div class="d-flex align-center ga-3 pa-4">
            <v-avatar color="primary" variant="tonal" size="48">
              <v-icon size="28">mdi-trophy-variant</v-icon>
            </v-avatar>
            <div class="flex-grow-1">
              <h2 class="text-h6 font-weight-bold">{{ $t('events.trackStandings') }}</h2>
              <div class="d-flex flex-wrap align-center ga-2 mt-1">
                <ChipEventType :type="data.event.eventType" />
                <v-chip :color="stateColor(data.event.state)" size="small" variant="tonal" label>
                  {{ $t(`eventStates.${data.event.state}`) }}
                </v-chip>
                <span class="text-caption text-medium-emphasis">
                  <DateRange :from="data.event.dateStart" :to="data.event.dateEnd" />
                </span>
                <span v-if="data.event.usesLaps" class="text-caption text-medium-emphasis">
                  {{ $t('events.laps') }}: {{ data.track.laps }}
                </span>
              </div>
            </div>
          </div>

          <v-divider />

          <v-table v-if="data.standings.length" hover>
            <thead>
              <tr>
                <th style="width: 56px" class="text-center">#</th>
                <th>{{ $t('events.player') }}</th>
                <th class="text-center">{{ $t(data.event.season ? 'events.rating' : 'events.time') }}</th>
                <th v-if="data.event.pointsBased" class="text-center">{{ $t('events.points') }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in data.standings" :key="row.userId" :class="{ 'me-row': row.isMe }">
                <td class="text-center">
                  <v-icon v-if="row.position <= 3" :class="podiumClass(row.position)" size="20">mdi-medal</v-icon>
                  <span v-else class="text-medium-emphasis">{{ row.position }}</span>
                </td>
                <td class="font-weight-medium"><UserName :name="row.playerName" /></td>
                <!-- A season is ranked on the rating, which starts at zero and can be negative -->
                <td class="text-center text-mono">
                  {{ data.event.season ? formatDelta(row.rating) : (row.resultTime ?? '–') }}<ProvisionalMark v-if="row.provisional" />
                </td>
                <td v-if="data.event.pointsBased" class="text-center font-weight-bold">
                  {{ row.points ?? '–' }}
                </td>
              </tr>
            </tbody>
          </v-table>

          <div v-else class="d-flex align-center ga-2 pa-4 text-medium-emphasis">
            <v-icon size="20">mdi-flag-outline</v-icon>
            <span class="text-caption">{{ $t('events.noTrackResults') }}</span>
          </div>
        </v-card>
      </template>
    </v-container>
  </main>
</template>

<style scoped>
.me-row td {
  background-color: rgba(var(--v-theme-primary), 0.06);
}
</style>
